import { Box, Button, Table, Text } from '@factorialco/deprecated-design-system'
import { useField } from 'formik'
import { SaleInvoiceComplianceExtraFieldsFieldFragment } from 'generated/resources/graphql'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import { toCents } from 'modules/core/lib/formatCurrencyUtils'
import i18n from 'modules/core/lib/i18n'

import { formattedAmount } from 'modules/banking/lib/paymentHelpers'
import { formatTaxRate } from 'modules/finance/lib/invoices'

import { LineItem } from './types'

interface LineItemRowTableProps {
  name: string
  onDelete: () => void
  onEdit: () => void
  currency?: string
  enableDelete?: boolean
  readOnly?: boolean
  locale: string
  complianceExtraFields: SaleInvoiceComplianceExtraFieldsFieldFragment[]
  complianceExtraFieldErrors: Record<string, string> | undefined
}

export const LineItemRowTable: React.FC<LineItemRowTableProps> = ({
  name,
  onDelete,
  onEdit,
  currency,
  enableDelete,
  readOnly = false,
  locale,
  complianceExtraFields,
  complianceExtraFieldErrors,
}) => {
  const [_, { value }] = useField<LineItem>(name)

  const hasError = Object.keys(complianceExtraFieldErrors ?? {}).length > 0

  return (
    <Table.Row>
      <Table.Cell>
        <Text.Normal
          size="300"
          weight="medium"
          lineClamp={2}
          color={hasError ? 'radicalRed600' : undefined}
        >
          {value?.description || ''}
        </Text.Normal>
      </Table.Cell>
      <Table.Cell>
        <Text.Normal size="300" weight="normal" color={hasError ? 'radicalRed600' : undefined}>
          {value?.quantity ?? ''}
        </Text.Normal>
      </Table.Cell>
      <Table.Cell>
        <Text.Normal size="300" weight="normal" color={hasError ? 'radicalRed600' : undefined}>
          {currency && locale && value?.amount
            ? formattedAmount({ amountCents: toCents(value?.amount), currency, locale })
            : ''}
        </Text.Normal>
      </Table.Cell>
      <Table.Cell>
        <Text.Normal size="300" weight="normal" color={hasError ? 'radicalRed600' : undefined}>
          {formatTaxRate(value?.taxRate?.rate)}
        </Text.Normal>
      </Table.Cell>
      {complianceExtraFields.map((field) => (
        <Table.Cell key={field.name}>
          <Box width="s120">
            <Text.Normal
              size="300"
              weight="normal"
              lineClamp={1}
              ellipsis
              color={hasError ? 'radicalRed600' : undefined}
            >
              {field.value}
            </Text.Normal>
          </Box>
        </Table.Cell>
      ))}
      {!readOnly && (
        <Table.Cell
          sticky={{
            position: 'right',
            delimiter: false,
          }}
        >
          <Box flexDirection="row" gap="s8" alignItems="center">
            <PopoverMenu type="list">
              <PopoverMenu.Trigger>
                <Button.Decorative type="secondary" iconName="ellipsis" />
              </PopoverMenu.Trigger>
              <PopoverMenu.Content side="bottom" align="end">
                <PopoverMenu.TouchableOption
                  onClick={() => onEdit()}
                  title={i18n.t('common.edit')}
                  icon="pencil"
                />

                {enableDelete && (
                  <PopoverMenu.TouchableOption
                    onClick={() => onDelete()}
                    title={i18n.t('finance.buttons.delete')}
                    icon="delete"
                    danger
                  />
                )}
              </PopoverMenu.Content>
            </PopoverMenu>
          </Box>
        </Table.Cell>
      )}
    </Table.Row>
  )
}
